import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { PricingService } from '@core-mkt/services/pricing/pricing.service';
import { WebinarScheduleService } from '@core-mkt/services/webinar-schedule/webinar-schedule.service';
import { faChevronDown, faChevronUp, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { VisionModalService } from '../../vision-modal/vision-modal.service';
import type { WebinarScheduleResponse } from '../../webinar-schedule/webinar-schedule';
import type { BundleSelectionMergedProduct } from '../bundle-builder/bundle-builder';

@Component({
  selector: 'bundle-product-card',
  templateUrl: './bundle-product-card.component.html',
  standalone: true,
  imports: [FontAwesomeModule],
})
export class BundleProductCardComponent {
  @Input() data: BundleSelectionMergedProduct;
  @Input() showHours = false;
  @Output() productSelectionChange = new EventEmitter<BundleSelectionMergedProduct>();
  warnIcon = faExclamationTriangle;
  caretIcon = faChevronDown;
  openCaretIcon = faChevronUp;
  showDescriptionAccordion = true;

  private modalService = inject(VisionModalService);
  private pricingService = inject(PricingService);
  private webinarScheduleService = inject(WebinarScheduleService);

  get deliveryMethod(): string {
    return this.data.deliveryType === 'webinar' ? 'Webinar' : 'Online Self-Study';
  }

  get hideScheduleCta(): boolean {
    return this.data.hideScheduleCta;
  }

  get requireLabel(): string {
    return this.data.required ? 'Required' : 'Elective';
  }

  get viewScheduleLabel(): string {
    return 'View Schedule';
  }

  get currentPrice(): string {
    if (!this.data.productData) {
      return '0';
    }
    return this.pricingService.getPriceFromParsedData(this.data.productData.cost, this.data.productData.maxCost);
  }

  get productTitle(): string {
    if (!this.data.productData) {
      return '';
    }
    return this.data.productData.productName;
  }

  get productDescription(): string {
    return this.data.productData?.productDescription || '';
  }

  onCheckboxChange(bundleProduct: BundleSelectionMergedProduct): void {
    bundleProduct.selected = !bundleProduct.selected;
    this.productSelectionChange.emit(bundleProduct);
  }

  activateModal(): void {
    const modalContent = `<h3 class="mb-2">Warning: You are adding a course for a state that has multiple regulators.</h3>
    <p>Before completing this course, please ensure your MLO license is issued by the regulator listed here. Completing a course that is approved for another regulator in this state may not satisfy your education requirements and may require you to complete additional courses.</p>`;
    this.modalService.openModal({ modalContent: modalContent, showModal: true });
  }

  /**
   * Displays the webinar schedule by triggering the webinar schedule service.
   *
   * @param event - The event object associated with the user interaction.
   * @param content - The content to be passed to the webinar schedule service.
   *
   * @remarks
   * This method prevents the default behavior of the event before invoking the service.
   */
  showWebinarSchedule(event: Event, content: WebinarScheduleResponse[]): void {
    event.preventDefault();
    this.webinarScheduleService.setShowWebinarSchedule(true, content);
  }
}
